import React, { createContext, useState, useEffect, ReactNode } from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { lightTheme, darkTheme } from "../../assets/styles/Theme.tsx";

// Define the theme mode type
type ThemeMode = "light" | "dark";

// Define the context type
interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
}

// Create the context with a default value
export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Define props for the provider component
interface ThemeProviderComponentProps {
  children: ReactNode;
}

export const ThemeProviderComponent = ({ children }: ThemeProviderComponentProps) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    // Check if the user has a theme preference stored in localStorage
    const storedMode = localStorage.getItem("themeMode") as ThemeMode | null;

    // If the user has set a preference, use it
    if (storedMode === "light" || storedMode === "dark") {
      return storedMode;
    }

    // Otherwise, check the system/device theme preference
    const systemTheme: ThemeMode = window.matchMedia("(prefers-color-scheme: dark)")
      .matches
      ? "dark"
      : "light";
    return systemTheme;
  });

  useEffect(() => {
    // Store the mode in localStorage whenever it changes
    localStorage.setItem("themeMode", mode);

    // Detect system theme changes if the user hasn't set a custom theme
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      if (!localStorage.getItem("themeMode")) {
        setMode(e.matches ? "dark" : "light");
      }
    };

    // Listen for changes in the system's theme preference
    mediaQuery.addEventListener("change", handleSystemThemeChange);

    // Cleanup the event listener when the component unmounts
    return () => {
      mediaQuery.removeEventListener("change", handleSystemThemeChange);
    };
  }, [mode]);

  const toggleTheme = () => {
    setMode((prevMode) => {
      const newMode: ThemeMode = prevMode === "light" ? "dark" : "light";
      localStorage.setItem("themeMode", newMode); // Save the updated mode to localStorage
      return newMode;
    });
  };

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <ThemeProvider theme={mode === "light" ? lightTheme : darkTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};